/**
 * Custom hook for managing Performance Settings state and API operations
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useQualityMeasureLabel } from '../../../lib/qualityLabels';
import {
  performanceSettingsAPI,
  SupplierPerformanceSettings,
  SupplierRatingThreshold,
  CreateRatingThresholdData,
} from '../../../lib/api';
import type { ScopeSelection } from '../../../lib/api/performanceSettings';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { useConfirm, useAlertDialog } from '../../../contexts/ConfirmDialogContext';
import { useViewKey } from '../../../contexts/ViewContext';
import {
  PerformanceFormData,
  ThresholdEditData,
} from './types';

/**
 * CR-7274: which settings row this hook reads and writes.
 *
 * `undefined` keeps the pre-existing behaviour — the caller's own
 * configuration context — so every existing consumer is unaffected. Passing a
 * scope is what lets the settings tab edit a GROUP default: one save that
 * reaches every site under it.
 */
export function usePerformanceSettings(scopeTarget?: ScopeSelection) {
  const confirm = useConfirm();
  // With no explicit scope, the server resolves the company from the VIEW
  // CONTEXT (`performance_config_company`), so the site switcher changes what
  // this endpoint returns without changing a single argument to it. Without
  // this key in `loadSettings`'s dependencies nothing refetched on a switch:
  // the tab kept showing the previous site's values, and its Inherited /
  // Overridden badge kept naming the previous site, until a page refresh.
  const viewKey = useViewKey();
  // CR-7271: a refused Supplier Grade Thresholds write is explained in the same
  // modal the OTIF and PPM grade sections use, not a toast in the page corner.
  // PR #1140 wired only those two hooks, so this third band editor kept
  // reporting a refusal far from the form the user was editing.
  const alertDialog = useAlertDialog();
  const qualityLabel = useQualityMeasureLabel();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SupplierPerformanceSettings | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Form state
  const [formData, setFormData] = useState<PerformanceFormData>({
    // KPI Target Values (for dashboard graphs)
    otif_target: '95.00',
    otd_target: '95.00',
    ppm_target: 500,
    // Color Thresholds
    ppm_excellent_threshold: 500,
    ppm_good_threshold: 2000,
    ppm_poor_threshold: 5000,
    otif_weight: '70.00',
    ppm_weight: '30.00',
    otif_days_early_allowed: 21,
    otif_days_late_allowed: 1,
    include_arrears_in_otif: true,
    arrears_buffer_days: 0,
    default_firm_days: 14,
    delivery_metric_mode: 'both',
  });

  // Threshold editing state
  const [editingThreshold, setEditingThreshold] = useState<number | null>(null);
  const [thresholdEditData, setThresholdEditData] = useState<ThresholdEditData | null>(null);
  const [savingThreshold, setSavingThreshold] = useState(false);

  // Add threshold state
  const [showAddThreshold, setShowAddThreshold] = useState(false);
  // The form holds everything the user types. `performance_settings` is not
  // typed here because it is not the user's to choose: it is the settings row
  // the page is already editing, supplied at the point of the POST.
  const [newThresholdData, setNewThresholdData] = useState<Omit<CreateRatingThresholdData, 'performance_settings'>>({
    grade: '',
    label: '',
    min_score: '0',
    max_score: '100',
    color: '#3B82F6',
    description: '',
    order: 0,
  });
  const [addingThreshold, setAddingThreshold] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await performanceSettingsAPI.getSettings(scopeTarget);
      setSettings(response.data);
      setFormData({
        // KPI Target Values (for dashboard graphs)
        otif_target: response.data.otif_target,
        otd_target: response.data.otd_target,
        ppm_target: response.data.ppm_target,
        // Color Thresholds
        ppm_excellent_threshold: response.data.ppm_excellent_threshold,
        ppm_good_threshold: response.data.ppm_good_threshold,
        ppm_poor_threshold: response.data.ppm_poor_threshold,
        otif_weight: response.data.otif_weight,
        ppm_weight: response.data.ppm_weight,
        otif_days_early_allowed: response.data.otif_days_early_allowed,
        otif_days_late_allowed: response.data.otif_days_late_allowed,
        include_arrears_in_otif: response.data.include_arrears_in_otif,
        arrears_buffer_days: response.data.arrears_buffer_days ?? 0,
        default_firm_days: response.data.default_firm_days ?? 14,
        delivery_metric_mode: response.data.delivery_metric_mode || 'both',
      });
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to load performance settings:', error);
      toast.error('Failed to load performance settings');
    } finally {
      setLoading(false);
    }
    // Re-runs whenever the chosen scope changes, which is what makes the
    // "Configure settings for" picker actually switch what is on screen.
    // Depending on the primitives rather than the object keeps this stable
    // across renders — an object literal would be a new reference each time
    // and refetch forever.
    //
    // `viewKey` is the second trigger and not the same thing: the scope is
    // what the user picked IN the tab, the view key is which site the whole
    // app is pointed at. A switch of the latter re-resolves the former's
    // default, so both have to refetch. It also reseeds `formData` and clears
    // `hasChanges`, which is what stops an unsaved edit made against one site
    // from sitting in the form over another site's values.
  }, [scopeTarget?.scope, scopeTarget?.id, viewKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const [clearingOverride, setClearingOverride] = useState(false);

  /**
   * CR-7274: stop overriding at the current scope so it inherits again.
   *
   * Reloads afterwards rather than patching state locally: what the screen
   * should now show is the INHERITED row, which only the server can resolve.
   */
  const clearOverride = useCallback(async () => {
    // The scope the BADGE is describing, which is not always one the user
    // picked. `scopeTarget` is set only by the "Configure settings for"
    // dropdown; arriving on the tab normally leaves it undefined while the
    // picker still names the site and the badge still reads "Overridden for
    // this site". Bailing out on that undefined made the Clear override button
    // a no-op on exactly the path most people take to it — enabled, clickable,
    // and silent: no request, no toast, no error.
    //
    // Named explicitly from `settings.company` rather than sent as a bare
    // DELETE. The server would resolve its own default context, but that is
    // re-deriving at request time what the screen already knows, and a view
    // context that moved between render and click would clear a row other than
    // the one whose badge the user was looking at.
    const target: ScopeSelection | undefined =
      scopeTarget
      ?? (settings?.company ? { scope: 'site', id: settings.company } : undefined);
    if (!target) return;
    // CR-7274 rework (tester observation 2): a Group Default can be cleared
    // too. Unlike a site override that is one site's business, this drops
    // every inheriting site back to the platform default, so it asks first.
    if (target.scope === 'group') {
      const ok = await confirm({
        title: 'Clear Group Default',
        description:
          'Every site that inherits this Group Default will fall back to the '
          + 'platform default. Sites with their own settings are not affected. Continue?',
        confirmLabel: 'Clear',
        variant: 'destructive',
      });
      if (!ok) return;
    }
    try {
      setClearingOverride(true);
      const response = await performanceSettingsAPI.clearOverride(target);
      const clearedMessage = target.scope === 'group'
        ? 'Group Default cleared — sites without their own settings now use the platform default.'
        : 'Override cleared — this scope now inherits.';
      toast.success(response.data.cleared ? clearedMessage : 'This scope already inherits.');
      await loadSettings();
    } catch (error: unknown) {
      console.error('Failed to clear override:', error);
      toast.error(getApiErrorMessage(error, 'Failed to clear override'));
    } finally {
      setClearingOverride(false);
    }
  }, [scopeTarget, settings?.company, loadSettings, confirm]);

  const handleInputChange = useCallback((field: string, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  }, []);

  const handleWeightChange = useCallback((field: 'otif_weight' | 'ppm_weight', value: string) => {
    const numValue = parseFloat(value) || 0;
    const otherField = field === 'otif_weight' ? 'ppm_weight' : 'otif_weight';
    const otherValue = (100 - numValue).toFixed(2);

    setFormData(prev => ({
      ...prev,
      [field]: value,
      [otherField]: otherValue,
    }));
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      const response = await performanceSettingsAPI.updateSettings(
        formData, scopeTarget);
      // The PATCH returns the full settings object directly; the previous
      // `response.data.data` read was undefined at runtime, blanking the
      // tab's state after every save.
      setSettings(response.data);
      setHasChanges(false);
      toast.success('Performance settings saved successfully');
    } catch (error: unknown) {
      console.error('Failed to save performance settings:', error);
      toast.error(getApiErrorMessage(error, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  }, [formData]);

  // Threshold operations
  const handleResetThresholds = useCallback(async () => {
    if (!settings) return;
    const ok = await confirm('Reset all rating thresholds to default values?');
    if (!ok) return;

    try {
      await performanceSettingsAPI.resetRatingThresholds(settings.id, scopeTarget);
      await loadSettings();
      toast.success('Rating thresholds reset to defaults');
    } catch (error) {
      console.error('Failed to reset thresholds:', error);
      toast.error('Failed to reset thresholds');
    }
  }, [settings, loadSettings, confirm, scopeTarget]);

  const handleResetOTIFMappings = useCallback(async () => {
    if (!settings) return;
    const ok = await confirm('Reset all OTIF score mappings to default values?');
    if (!ok) return;

    try {
      await performanceSettingsAPI.resetOTIFScoreMappings(settings.id, scopeTarget);
      await loadSettings();
      toast.success('OTIF score mappings reset to defaults');
    } catch (error) {
      console.error('Failed to reset OTIF mappings:', error);
      toast.error('Failed to reset OTIF mappings');
    }
  }, [settings, loadSettings, confirm, scopeTarget]);

  const handleResetPPMMappings = useCallback(async () => {
    if (!settings) return;
    const ok = await confirm(`Reset all ${qualityLabel} score mappings to default values?`);
    if (!ok) return;

    try {
      await performanceSettingsAPI.resetPPMScoreMappings(settings.id, scopeTarget);
      await loadSettings();
      toast.success(`${qualityLabel} score mappings reset to defaults`);
    } catch (error) {
      console.error(`Failed to reset ${qualityLabel} mappings:`, error);
      toast.error(`Failed to reset ${qualityLabel} mappings`);
    }
  }, [settings, loadSettings, confirm, scopeTarget]);

  const handleEditThreshold = useCallback((threshold: SupplierRatingThreshold) => {
    setEditingThreshold(threshold.id);
    setThresholdEditData({
      grade: threshold.grade,
      label: threshold.label || '',
      min_score: threshold.min_score,
      max_score: threshold.max_score,
      color: threshold.color,
      description: threshold.description,
      order: threshold.order,
    });
  }, []);

  const handleCancelEditThreshold = useCallback(() => {
    setEditingThreshold(null);
    setThresholdEditData(null);
  }, []);

  const handleSaveThreshold = useCallback(async (thresholdId: number) => {
    if (!thresholdEditData) return;

    try {
      setSavingThreshold(true);
      await performanceSettingsAPI.updateRatingThreshold(thresholdId, {
        grade: thresholdEditData.grade,
        label: thresholdEditData.label,
        min_score: thresholdEditData.min_score,
        max_score: thresholdEditData.max_score,
        color: thresholdEditData.color,
        description: thresholdEditData.description,
        order: thresholdEditData.order,
      }, scopeTarget);
      await loadSettings();
      setEditingThreshold(null);
      setThresholdEditData(null);
      toast.success('Grade threshold updated successfully');
    } catch (error: unknown) {
      console.error('Failed to update threshold:', error);
      await alertDialog({ title: 'Grade threshold not saved', description: getApiErrorMessage(error, 'Failed to update threshold') });
    } finally {
      setSavingThreshold(false);
    }
  }, [thresholdEditData, loadSettings, alertDialog, scopeTarget]);

  const handleThresholdFieldChange = useCallback((field: string, value: string | number) => {
    setThresholdEditData(prev => prev ? { ...prev, [field]: value } : null);
  }, []);

  const handleAddThreshold = useCallback(async () => {
    if (!settings) return;
    if (!newThresholdData.grade.trim()) {
      toast.error('Please enter a grade identifier');
      return;
    }

    try {
      setAddingThreshold(true);
      const maxOrder = settings.rating_thresholds?.reduce((max, t) => Math.max(max, t.order), 0) || 0;
      await performanceSettingsAPI.createRatingThreshold({
        ...newThresholdData,
        // CR-7271: the serializer requires this before it validates the band,
        // so without it Add Grade refused every attempt with "This field is
        // required." and could never create a grade.
        performance_settings: settings.id,
        order: maxOrder + 1,
      }, scopeTarget);
      await loadSettings();
      setShowAddThreshold(false);
      setNewThresholdData({
        grade: '',
        label: '',
        min_score: '0',
        max_score: '100',
        color: '#3B82F6',
        description: '',
        order: 0,
      });
      toast.success('New grade threshold added successfully');
    } catch (error: unknown) {
      console.error('Failed to add threshold:', error);
      await alertDialog({ title: 'Grade threshold not added', description: getApiErrorMessage(error, 'Failed to add threshold') });
    } finally {
      setAddingThreshold(false);
    }
  }, [newThresholdData, settings, loadSettings, alertDialog, scopeTarget]);

  const handleDeleteThreshold = useCallback(async (thresholdId: number, label: string) => {
    const ok = await confirm({ title: 'Delete Threshold', description: `Are you sure you want to delete the "${label}" grade threshold?`, confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) {
      return;
    }

    try {
      await performanceSettingsAPI.deleteRatingThreshold(thresholdId, scopeTarget);
      await loadSettings();
      toast.success('Grade threshold deleted successfully');
    } catch (error: unknown) {
      console.error('Failed to delete threshold:', error);
      await alertDialog({ title: 'Grade threshold not deleted', description: getApiErrorMessage(error, 'Failed to delete threshold') });
    }
  }, [loadSettings, confirm, alertDialog, scopeTarget]);

  const handleNewThresholdFieldChange = useCallback((field: string, value: string | number) => {
    setNewThresholdData(prev => ({ ...prev, [field]: value }));
  }, []);

  return {
    // CR-7274 scope
    clearOverride,
    clearingOverride,
    // State
    loading,
    saving,
    settings,
    hasChanges,
    formData,
    // Threshold state
    editingThreshold,
    thresholdEditData,
    savingThreshold,
    showAddThreshold,
    newThresholdData,
    addingThreshold,
    // State setters
    setShowAddThreshold,
    // Handlers
    handleInputChange,
    handleWeightChange,
    handleSave,
    handleResetThresholds,
    handleResetOTIFMappings,
    handleResetPPMMappings,
    handleEditThreshold,
    handleCancelEditThreshold,
    handleSaveThreshold,
    handleThresholdFieldChange,
    handleAddThreshold,
    handleDeleteThreshold,
    handleNewThresholdFieldChange,
    loadSettings,
  };
}
